import React, { useState } from 'react';
import logo from '../../assets/Logo.png';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const MainHeader = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { t } = useTranslation();

  const navLinks = [
    { key: 'accueil', path: '/' },
    { key: 'apropos', path: '#about' },
    { key: 'services', path: '#services' },
    { key: 'contact', path: '#contact' },
  ];

  return (
    <header className="sticky top-0 z-50 transition-all duration-500 bg-white/95 backdrop-blur-xl shadow-2xl border-b border-[#4f837d]/10">
      <div className="container mx-auto px-6 max-w-screen-xl w-full">
        <div className="flex justify-between items-center py-4 w-full">
          {/* Logo modifié - sans cadre rond */}
          <div className="flex items-center flex-shrink-0">
            <img src={logo} alt="Logo" className="h-10 sm:h-12 object-contain" />
          </div>

          {/* Menu Desktop modifié */}
          <nav className="hidden md:flex items-center space-x-6 justify-end flex-grow">
            {navLinks.map((link) => (
              <motion.a
                key={link.key}
                href={link.path}
                className="relative group py-1.5 px-3"
                whileHover={{ y: -2 }}
                transition={{ type: 'spring', stiffness: 300 }}
              >
                <span className="relative z-10 text-[#002B5C] font-medium text-sm tracking-wide group-hover:text-white transition-colors duration-300">
                  {t(`navigation.${link.key}`)}
                </span>

                {/* Fond animé - Dégradé harmonisé */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#4f837d] to-[#2e79b9] rounded-lg scale-0 group-hover:scale-95 transition-transform duration-300 shadow-sm"></div>

                {/* Effet de lueur */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#4f837d]/30 to-[#2e79b9]/30 rounded-lg scale-0 group-hover:scale-110 transition-transform duration-500 blur-sm opacity-0 group-hover:opacity-50"></div>
              </motion.a>
            ))}
          </nav>

          {/* Hamburger Premium modifié */}
          <motion.button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden relative p-2 rounded-lg bg-gradient-to-r from-[#4f837d] to-[#2e79b9] text-white shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-[#2e79b9]/20 ml-4"
            whileTap={{ scale: 0.95 }}
            whileHover={{ scale: 1.05 }}
            aria-label="Toggle menu"
          >
            <motion.div
              animate={isMenuOpen ? { rotate: 180 } : { rotate: 0 }}
              transition={{ duration: 0.3 }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <motion.line
                  x1="3"
                  y1="12"
                  x2="21"
                  y2="12"
                  animate={isMenuOpen ? { rotate: 45, y: 0 } : { rotate: 0, y: -6 }}
                />
                <motion.line
                  x1="3"
                  y1="6"
                  x2="21"
                  y2="6"
                  animate={isMenuOpen ? { opacity: 0 } : { opacity: 1 }}
                />
                <motion.line
                  x1="3"
                  y1="18"
                  x2="21"
                  y2="18"
                  animate={isMenuOpen ? { rotate: -45, y: 0 } : { rotate: 0, y: 6 }}
                />
              </svg>
            </motion.div>

            {/* Effet de pulsation */}
            <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-[#4f837d] to-[#2e79b9] animate-ping opacity-10"></div>
          </motion.button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: 'easeInOut' }}
            className="md:hidden overflow-hidden bg-gradient-to-br from-white via-[#4f837d]/5 to-white backdrop-blur-xl border-t border-[#4f837d]/10"
          >
            <div className="p-6 space-y-2">
              {navLinks.map((link, index) => (
                <motion.a
                  key={link.key}
                  href={link.path}
                  onClick={() => setIsMenuOpen(false)}
                  className="group relative block p-3 rounded-xl bg-white/60 backdrop-blur-sm border border-[#4f837d]/10 hover:border-[#2e79b9]/30 transition-all duration-300"
                  initial={{ opacity: 0, x: -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.01, x: 5 }}
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-1.5 h-1.5 bg-gradient-to-r from-[#4f837d] to-[#2e79b9] rounded-full group-hover:scale-125 transition-transform duration-300"></div>
                    <span className="text-[#002B5C] font-medium text-sm group-hover:text-[#4f837d] transition-colors duration-300">
                      {t(`navigation.${link.key}`)}
                    </span>
                  </div>

                  {/* Effet de lueur mobile */}
                  <div className="absolute inset-0 bg-gradient-to-r from-[#4f837d]/5 to-[#2e79b9]/5 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </motion.a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default MainHeader;